import { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { closeDialog } from '../app/dialogs';
import { dayWords, whatsAppNumber } from '../app/words';
import { dayOf } from '@day';
import type { Model } from '../data/model';
import { track } from '../data/analytics';
import { copyText } from '../pages/StopBody';
import { Icon } from './Icon';

/**
 * Invite a courier from the web, through the same `inviteCreate` function the phone uses (functions/src/invites.ts): a
 * single-use code for 7 days, with the server's own message and goroutr.com/j/ link, to copy or send on WhatsApp from
 * this computer. A business can begin with no phone at all.
 */
export function InviteCourier({ m }: { m: Model }) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [made, setMade] = useState<{ code: string; link: string; message: string; expiresAt: number } | null>(null);
  const make = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await httpsCallable<object, { code: string; link: string; message: string; expiresAt: number }>(functions, 'inviteCreate')({
        ...(name.trim() ? { name: name.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        channel: phone.trim() ? 'whatsapp' : 'share',
      });
      setMade(r.data);
      track('invite_created', { via: 'web', with_phone: !!phone.trim(), with_name: !!name.trim() });
    } catch (e) {
      const code = String((e as { code?: string }).code ?? '');
      const msg = String((e as { message?: string }).message ?? '');
      setError(
        msg.includes('bad-phone')
          ? 'That isn’t a phone number Routr can send to. Check it, or leave it out and copy the link instead.'
          : code.endsWith('unavailable') || code.endsWith('internal')
            ? 'Routr’s server didn’t answer. Check the connection and try again.'
            : 'Couldn’t make the invite. Try again in a moment.',
      );
    } finally {
      setBusy(false);
    }
  };
  const number = whatsAppNumber(phone.trim() || undefined, m.business?.country ?? null);
  return (
    <>
      <div className="title">Invite a courier</div>
      {!made ? (
        <>
          <div className="cap" style={{ marginTop: 4 }}>
            They get a link to join {m.business?.name ?? 'your business'} in the Routr app. It works once, for 7 days.
          </div>
          <div className="planedit" style={{ marginTop: 14 }}>
            <input data-first placeholder="Their name (optional)" value={name} onChange={(e) => setName(e.target.value)} autoComplete="off" autoCorrect="off" spellCheck={false} aria-label="Their name" />
            <input placeholder="Their phone, to send it on WhatsApp (optional)" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="off" aria-label="Their phone" />
          </div>
          {error && (
            <div className="gate-error" role="alert">
              {error}
            </div>
          )}
          <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={closeDialog}>
              Cancel
            </button>
            <button className="btn primary" disabled={busy} onClick={() => void make()}>
              <Icon name="userPlus" />
              {busy ? 'Making it…' : 'Make the invite'}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="cap" style={{ marginTop: 4 }}>
            Code <b className="mono">{made.code}</b> · works once, until {dayWords(dayOf(made.expiresAt, m.zone))}.
          </div>
          <div className="notecard" style={{ marginTop: 12, whiteSpace: 'pre-wrap' }}>{made.message}</div>
          <div className="row" style={{ marginTop: 18, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <button className="btn" onClick={() => copyText(made.message, 'The invite is copied')}>
              <Icon name="copy" />
              Copy the invite
            </button>
            <a
              className="btn primary"
              href={`https://wa.me/${number ?? ''}?text=${encodeURIComponent(made.message)}`}
              target="_blank"
              rel="noopener"
              onClick={() => track('invite_sent', { via: 'whatsapp', with_number: !!number })}
            >
              <Icon name="paperPlaneTilt" />
              Send on WhatsApp
            </a>
          </div>
          <div className="cap" style={{ marginTop: 8, textAlign: 'right' }}>
            {number ? 'WhatsApp opens on this computer with the message ready.' : 'WhatsApp opens on this computer; pick who it’s for.'}
          </div>
        </>
      )}
    </>
  );
}
